/**
 * Operation 模块 API 工厂函数
 *
 * @description 全局工序定义模块的 API 工厂函数
 * @module api/operation/factory
 * @category Operation - 工序管理
 *
 * @source 对应后端文件:
 * - backend/routes/api.php (路由：/api/v1/operations)
 * - backend/app/Modules/Operation/Controllers/OperationController.php
 *
 * @last-updated 2025-12-12
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  Operation,
  OperationStatistics,
  OperationGlobalStatistics,
  OperationQueryParams,
  OperationAllQueryParams,
  CreateOperationForm,
  UpdateOperationForm,
  UpdateOperationStatusForm,
} from '../../types/operation';

import type { PaginationResult } from '../../types/api';

// ============================================================================
// 导入统一的请求客户端接口
// ============================================================================
import type { IRequestClient } from '../core/client';
import { API_ROUTES } from '../routes';

// ============================================================================
// Operation API 工厂函数
// ============================================================================

/**
 * 创建 Operation 模块的 API 方法集合
 *
 * @param client 请求客户端（必须符合 IRequestClient 接口）
 * @returns Operation 模块的所有 API 方法
 *
 * @example
 * import { createOperationApi } from '@bayes-mes/shared/api/operation/factory'
 * import { webAdapter } from '@/api/adapters/http'
 *
 * export const operationApi = createOperationApi(webAdapter)
 */
export function createOperationApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 工序 CRUD 操作
    // ========================================================================

    /**
     * 获取工序列表（分页）
     *
     * @param params 查询参数
     * @returns 分页的工序列表
     */
    getOperationList(params?: OperationQueryParams) {
      return client.get<PaginationResult<Operation>>(API_ROUTES.operation.list, { params });
    },

    /**
     * 获取所有工序（用于下拉选择）
     *
     * @param params 查询参数
     * @returns 工序列表
     */
    getAllOperations(params?: OperationAllQueryParams) {
      return client.get<Operation[]>(API_ROUTES.operation.all, { params });
    },

    /**
     * 获取工序详情
     *
     * @param id 工序ID
     * @returns 工序详情
     */
    getOperationDetail(id: number) {
      return client.get<Operation>(API_ROUTES.operation.detail(id));
    },

    /**
     * 创建工序
     *
     * @param data 工序表单数据
     * @returns 创建的工序信息
     */
    createOperation(data: CreateOperationForm) {
      return client.post<Operation>(API_ROUTES.operation.create, data);
    },

    /**
     * 更新工序
     *
     * @param id 工序ID
     * @param data 更新数据
     * @returns 更新后的工序信息
     */
    updateOperation(id: number, data: UpdateOperationForm) {
      return client.put<Operation>(API_ROUTES.operation.operations(id), data);
    },

    /**
     * 删除工序
     *
     * @param id 工序ID
     */
    deleteOperation(id: number) {
      return client.delete(API_ROUTES.operation.delete(id));
    },

    /**
     * 批量删除工序
     *
     * @param ids 工序ID数组
     * @returns 删除结果
     */
    batchDeleteOperations(ids: number[]) {
      return client.post<{
        deleted: number;
        failed: number;
        errors: string[];
        total: number;
      }>(API_ROUTES.operation.batchDelete, { ids });
    },

    // ========================================================================
    // 工序状态管理
    // ========================================================================

    /**
     * 更新工序状态
     *
     * @param id 工序ID
     * @param data 状态数据
     * @returns 更新后的工序信息
     */
    updateOperationStatus(id: number, data: UpdateOperationStatusForm) {
      return client.put<Operation>(API_ROUTES.operation.update(id), data);
    },

    // ========================================================================
    // 工序统计信息
    // ========================================================================

    /**
     * 获取工序全局统计信息
     *
     * @returns 全局统计信息
     */
    getOperationGlobalStatistics() {
      return client.get<OperationGlobalStatistics>(API_ROUTES.operation.statistics);
    },

    /**
     * 获取单个工序统计信息
     *
     * @param id 工序ID
     * @returns 工序统计信息
     */
    getOperationStatistics(id: number) {
      return client.get<OperationStatistics>(API_ROUTES.operation.operationsStatistics(id));
    },

    // ========================================================================
    // 工序查询
    // ========================================================================

    /**
     * 通过工序编码查找工序
     *
     * @param code 工序编码
     * @returns 工序详情
     */
    getOperationByCode(code: string) {
      return client.get<Operation>(API_ROUTES.operation.findByCode(code));
    },

    /**
     * 获取工序类型列表
     *
     * @returns 工序类型映射 { 'PROCESSING': '加工', ... }
     */
    getOperationTypes() {
      return client.get<Record<string, string>>(API_ROUTES.operation.types);
    },

    /**
     * 获取检验类型列表
     *
     * @returns 检验类型映射 { 'NONE': '无需检验', ... }
     */
    getInspectionTypes() {
      return client.get<Record<string, string>>(API_ROUTES.operation.inspectionTypes);
    },

    // ========================================================================
    // 导入导出
    // ========================================================================

    /**
     * 导入工序
     *
     * @param file 文件对象
     * @returns 导入结果 { success: number, failed: number, errors: string[] }
     */
    importOperations(file: File) {
      const formData = new FormData();
      formData.append('file', file);
      return client.post<{
        success: number;
        failed: number;
        errors: string[];
        total_errors: number;
      }>(API_ROUTES.operation.import, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    /**
     * 导出工序（返回下载链接或触发下载）
     *
     * @param params 筛选参数
     * @returns Blob 文件流
     */
    exportOperations(params?: { operation_type?: string; status?: string }) {
      return client.get<Blob>(API_ROUTES.operation.export, {
        params,
        responseType: 'blob',
      });
    },

    /**
     * 下载导入模板
     *
     * @returns Blob 文件流
     */
    downloadImportTemplate() {
      return client.get<Blob>(API_ROUTES.operation.template, {
        responseType: 'blob',
      });
    },
  };
}

// ============================================================================
// 类型导出
// ============================================================================

/**
 * Operation API 实例类型
 */
export type OperationApi = ReturnType<typeof createOperationApi>;
